import { useState, useCallback, useEffect } from 'react';
import { useFetch } from './useFetch';
import API_ROUTES from './apiRoutes';

interface UserProfile {
  id: string;
  name: string;
  email: string;
  role: string;
}

interface ProfileState {
  user: UserProfile | null;
  isLoading: boolean;
  error: string | null;
  success: boolean;
}

export const useProfile = () => {
  const [state, setState] = useState<ProfileState>({
    user: null,
    isLoading: false,
    error: null,
    success: false,
  });

  const { fetchWithAuth } = useFetch();

  // Buscar dados do usuário da API usando a rota /users/profile
  const fetchUserProfile = useCallback(async (): Promise<void> => {
    setState((prev) => ({ ...prev, isLoading: true, error: null }));

    try {
      const token = localStorage.getItem('accessToken');
      if (!token) {
        throw new Error('Token não encontrado');
      }

      // Buscar perfil do usuário logado da API
      const response = await fetchWithAuth(API_ROUTES.USERS.profile(), {
        method: 'GET',
      });

      if (!response.ok) {
        throw new Error(`Erro ao buscar perfil: ${response.status}`);
      }

      const userData = await response.json();

      setState((prev) => ({
        ...prev,
        user: {
          id: userData.id,
          name: userData.name,
          email: userData.email,
          role: userData.role,
        },
        isLoading: false,
      }));
    } catch (error) {
      setState((prev) => ({
        ...prev,
        error: error instanceof Error ? error.message : 'Erro desconhecido',
        isLoading: false,
      }));
    }
  }, [fetchWithAuth]);

  // Atualizar nome do usuário usando a rota /users/profile
  const updateUserName = useCallback(async (newName: string): Promise<boolean> => {
    if (!state.user) {
      setState((prev) => ({ ...prev, error: 'Usuário não carregado' }));
      return false;
    }

    setState((prev) => ({ ...prev, isLoading: true, error: null, success: false }));

    try {
      const response = await fetchWithAuth(API_ROUTES.USERS.profile(), {
        method: 'PUT',
        body: JSON.stringify({ name: newName }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Erro ao atualizar nome: ${response.status} - ${errorText}`);
      }

      // Atualizar estado local
      setState((prev) => ({
        ...prev,
        user: prev.user ? { ...prev.user, name: newName } : null,
        isLoading: false,
        success: true,
      }));

      return true;
    } catch (error) {
      setState((prev) => ({
        ...prev,
        error: error instanceof Error ? error.message : 'Erro desconhecido',
        isLoading: false,
        success: false,
      }));
      return false;
    }
  }, [state.user, fetchWithAuth]);

  // Carregar perfil automaticamente se tiver token
  useEffect(() => {
    const token = localStorage.getItem('accessToken');
    if (token && !state.user) {
      fetchUserProfile();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    user: state.user,
    isLoading: state.isLoading,
    error: state.error,
    success: state.success,
    fetchUserProfile,
    updateUserName,
  };
};

